import { useMutation } from '@tanstack/react-query';
import { Button, Modal, Tag, Typography } from '@volvo/vcdk-react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { IFleetMember } from '@/service/vpc/types/IFleetMember';
import type { ColumnDef } from '@tanstack/react-table';
import type { Dispatch } from 'react';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import Table from '@/components/Table';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { vpcService } from '@/service/vpc/vpcService';

type Props = {
  members: IFleetMember[];
  isOpen: boolean;
  setIsOpen: Dispatch<React.SetStateAction<boolean>>;
};

const RemoveMembersModal: React.FC<Props> = ({ members, isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const { fleetAccountId } = useFleetAccount();

  const { mutateAsync: disconnectMembers, isPending } = useMutation({
    mutationFn: ({ fleetAccountId, members }: { fleetAccountId: string; members: IFleetMember[] }) =>
      vpcService.FleetAccount.disconnectEndUser({ fleetAccountId, members }),
  });

  const handleOnClose = () => {
    // setRolesValue(initialRoles.map((role) => role));
    setIsOpen(false);
  };

  const handleRemove = async () => {
    await disconnectMembers({ fleetAccountId: fleetAccountId!, members });
    handleOnClose();
  };

  const columns = [
    {
      accessorFn: (row) => row.firstName + ' ' + row.surname,
      id: 'name',
      header: t(($) => $.components.table.members_table.name),
      size: 5,
      cell: ({ row }) => {
        const { firstName, surname } = row.original;
        const fullName = `${firstName} ${surname}`;
        return <Typography>{fullName}</Typography>;
      },
    },
    {
      accessorKey: 'email',
      id: 'email',
      header: t(($) => $.components.table.members_table.email),
      size: 30,
      cell: (info) => {
        const email = info.getValue<string>();
        return <Typography>{email}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.roles.join(', '),
      id: 'account_roles',
      header: t(($) => $.components.table.members_table.account),
      size: 40,
      cell: (info) => {
        const { roles, email } = info.row.original;
        const visibleRoles = roles.filter((role) => role !== 'Member');
        return (
          <div className="mt-5 flex flex-wrap gap-3 md:mt-0">
            {visibleRoles.map((role) => (
              <Tag key={`${email}-${role}`} variant="neutral-subtle">
                {role}
              </Tag>
            ))}
          </div>
        );
      },
    },
  ] as ColumnDef<IFleetMember>[];

  return (
    <>
      {isOpen &&
        createPortal(
          <Modal open={isOpen} onClose={handleOnClose} maxWidth="90%" width={700}>
            <span slot="header">{t(($) => $.components.modal.remove_members_modal.title)}</span>
            <div className="flex flex-col gap-6">
              <Typography>
                {t(($) => $.components.modal.remove_members_modal.description, {
                  count: members.length,
                })}
              </Typography>
              <Typography>{t(($) => $.components.modal.remove_members_modal.are_you_sure)}</Typography>
              <Table
                isLoading={false}
                data={members}
                columns={columns}
                showHeader={false}
                showPagination={false}
                showSearch={false}
              />
            </div>
            <span slot="modalFooter">
              <div className="flex flex-col-reverse gap-4 md:flex-row">
                <Button
                  variant="secondary"
                  onClick={handleOnClose}
                  disabled={isPending}
                  className="w-full flex-1"
                >
                  {t(($) => $.components.modal.remove_members_modal.cancel)}
                </Button>
                <Button
                  variant="primary"
                  disabled={isPending}
                  onClick={handleRemove}
                  className="w-full flex-1"
                >
                  {t(($) => $.components.modal.remove_members_modal.submit)}
                </Button>
              </div>
            </span>
          </Modal>,
          document.body
        )}
    </>
  );
};

export default withComponentErrorBoundary(RemoveMembersModal);
